// =============================================================================
// domain/leaks.ts — the registry, and the ranking Spot it draws.
// -----------------------------------------------------------------------------
// Each leak declares a RATE and a TOTAL (see `leakSequence.ts`). This file binds
// them into one list and answers the question the report opens with: what is
// costing you most?
//
// ── 🚨 `kind` IS WHY THERE ARE TWO LISTS, NOT ONE ──
//
//   realized     — dollars actually lost. Already booked, already gone.
//   opportunity  — a COUNTERFACTUAL: what a different decision MIGHT have been
//                  worth. Always an upper bound, never recoverable P&L.
//
// They cannot share a ranking. An opportunity figure is unbounded above — "you
// left $27,990 on the table" — and would out-rank every real loss in the book,
// putting a hypothetical at the top of a report whose whole promise is "this is
// money already gone". The two lists are the honest form of the same data, and
// the old client separates them for exactly this reason.
//
// ── 🚨 RANKING USES DOLLARS. PROVING USES THE RATE ──
//
// Restated here because the two live in different files and the mistake is
// invisible: a ten-day "since" window always shows a smaller TOTAL than a
// sixty-day baseline, so proving on totals manufactures an improvement out of a
// shorter window. Ranking has no such problem — it compares leaks inside ONE
// window.
//
// ── ⚠ ALL SEVEN NOW MEASURE, AND THE SEVENTH READS A STORED WALK ──
//
// `premature-exit` was declared unavailable while the post-exit walk was
// unported. It now reads `extras.extensions` — the result the old client's
// metered bar fetch leaves behind — so nothing here fetches anything, and a
// trade whose extension was never computed simply leaves the population.
//
// ⚠ THAT IS A DIFFERENT SILENCE FROM THE OLD ONE, and the row says so: it used
// to read "not measured in this client yet" and now reads "not enough data to
// measure this period" until the extensions exist. See `domain/leakExtension.ts`
// for why the naive version of this number is wrong by more than 2×.
// =============================================================================

import {
  controllableLossReasons, isControllableLoss, type ControllableTrade,
} from './dayReport';
import { mfeCapture, type CaptureTrade } from './capture';
import {
  prematureExitNet, prematureExitValue, type ExtensionTrade,
} from './leakExtension';
import type { ContractSpec } from './trade';
import {
  addToLoserDollars, addToLoserRate, addToLoserStats, type AddToLoserTrade,
  planShortfallDollars, planShortfallRate, planStats, type PlanTrade,
} from './leakPlan';
import {
  dispositionDollars, dispositionRate, dispositionStats, holdMinutes,
  tiltContributors, tiltDollars, tiltDrop,
  type LeakDollars, type LeakRate, type SequenceTrade,
} from './leakSequence';

/**
 * Everything any leak reads.
 *
 * ⚠ ONE type rather than a union, because the registry runs every leak over the
 * same list. A leak that cannot assess a trade drops it from its OWN population
 * — that decision belongs to the measure, not to the caller.
 */
export interface LeakTrade
  extends SequenceTrade, AddToLoserTrade, PlanTrade, CaptureTrade,
  ControllableTrade, ExtensionTrade {
  controllableLossAmount?: number | null;
}

export type LeakKind = 'realized' | 'opportunity';

/**
 * What the measures need beyond the trades themselves.
 *
 * ── 🚨 THE CONTROLLABLE-LOSS LEAK DEPENDS ON THE USER'S OWN SETTINGS ──
 *
 * Which mistake tags count as "controllable" is a per-user map, editable in
 * Settings. So this leak's number MOVES when the trader unticks a tag — by
 * design, and it is why the config is threaded in explicitly rather than read
 * from a module-level default. A default would make the report quietly disagree
 * with the setting the trader just changed, and nothing on screen would explain
 * the difference.
 */
export interface LeakContext {
  /** `controllableMistakeTags` — sparse, opt-outs only, absent means true. */
  controllableTags: Record<string, unknown> | null | undefined;
  /**
   * The instrument catalogue — ONLY the premature-exit leak needs it, and only
   * as a fallback.
   *
   * ⚠ OPTIONAL, and an empty list is a legitimate value. That leak recovers its
   * dollars-per-point from the stored run's own points/dollars pair, which is
   * exact; the catalogue covers the one case that ratio cannot price — a trade
   * stopped on its first bar, whose run is exactly zero. See
   * `domain/leakExtension.ts`.
   */
  contracts?: readonly ContractSpec[] | undefined;
}

/**
 * The extra column on a leak's review list — the number that flagged each trade.
 *
 * ── ⚠ A VALUE, NEVER A FORMATTED STRING ──
 *
 * The old client's version returns finished HTML (`fmtMoneyPlain(…)`), which
 * cannot live here: `domain/` holds the arithmetic and `ui/` holds the money and
 * clock formatting, and a domain module that imported a formatter would drag the
 * display layer into the one place that has to compile inside an Edge Function.
 *
 * So the leak declares WHAT the number is (`unit`) and hands back the raw value;
 * the table decides how to print it. `null` means this trade has no value for
 * the column — the row shows an em dash rather than a zero, for the same reason
 * a leak row does.
 */
export interface LeakReviewColumn {
  /** ⚠ May carry a computed figure — "Held vs 23m" names the bar each row beat. */
  header: string;
  tone?: 'green' | 'red';
  unit: 'usd' | 'min' | 'r' | 'text';
  value(t: LeakTrade): number | string | null;
}

export interface LeakMetric {
  id: string;
  label: string;
  /** One sentence, in the trader's language, for the row's ? mark. */
  help: string;
  /**
   * The explainer behind the row's `?`.
   *
   * ⚠ DECLARED, NOT DERIVED. Every key happens to be `coach.leak.${id}` today, and
   * computing it would make a typo in either registry produce a silently missing
   * mark. Written out, a test can walk the list and assert each one resolves.
   */
  eduKey: string;
  kind: LeakKind;
  /** Which direction is an improvement. */
  better: 'lower' | 'higher';
  /** The best attainable value, where one exists. A baseline sitting on it is unimprovable. */
  floor?: number;
  ceiling?: number;
  unit: 'usd' | 'pct' | 'min';
  /** ⚠ Gates the leak's OWN qualifying sample, not the raw trade count. */
  minSample: number;
  /** What this leak needs that the trader may not have. */
  needs?: string;
  /** False when this client cannot measure it at all — see the header. */
  available: boolean;
  /**
   * False when the RATE measures but the dollar face does not.
   *
   * ⚠ A THIRD STATE, and it needed its own flag rather than being inferred from
   * a zero. Capture efficiency reports a real percentage over a real sample and
   * has no dollar total here, so a reader of `dollars === 0` alone cannot tell
   * it from a leak that genuinely cost nothing — and the row rendered
   * "No leak here this period — measured over 0 trades", a sentence that
   * contradicts itself. Found by opening the tab, not by a test.
   */
  dollarsAvailable: boolean;
  measure(trades: readonly LeakTrade[], ctx: LeakContext): LeakRate;
  dollarImpact(trades: readonly LeakTrade[], ctx: LeakContext): LeakDollars;
  /**
   * The exact trades behind the dollar figure — what the review list shows.
   *
   * ── 🚨 THIS MUST AGREE WITH `dollarImpact().n`, AND A TEST PINS IT ──
   *
   * The row says "12 of 85 trades" and the link opens the list. If the two are
   * computed from different populations the screen contradicts itself the moment
   * either changes, and the trader is reading a set of trades that is not the set
   * the number was drawn from — a difference nothing on screen could explain.
   *
   * ⚠ EMPTY IS A REAL ANSWER, not a stub. A leak this client cannot measure has
   * no trades behind it, and offering a review link into nothing is worse than
   * offering none.
   */
  contributors(trades: readonly LeakTrade[], ctx: LeakContext): LeakTrade[];
  /** The column that says WHY each trade made the list. Omitted where the leak's own name already says it. */
  reviewColumn?(trades: readonly LeakTrade[], ctx: LeakContext): LeakReviewColumn;
}

const NOT_MEASURED: LeakRate = { value: null, n: 0 };
const NO_DOLLARS: LeakDollars = { dollars: 0, n: 0, population: 0 };

const controllableAmount = (t: LeakTrade): number =>
  Math.abs(Number(t?.controllableLossAmount) || 0);

/**
 * The seven, in the old client's order.
 *
 * ⚠ ORDER IS NOT RANKING. This is the registry's declaration order; the report
 * ranks by dollars within a kind. Keeping it aligned with the old client makes
 * a side-by-side readable.
 */
export const LEAK_METRICS: readonly LeakMetric[] = [
  {
    id: 'controllable-loss', eduKey: 'coach.leak.controllable-loss',
    label: 'Controllable losses',
    help: 'Losses you tagged as revenge, FOMO, overtrading, or a mistake.',
    kind: 'realized', better: 'lower', floor: 0, unit: 'usd', minSample: 20,
    available: true, dollarsAvailable: true,
    measure(trades) {
      if (!trades.length) return NOT_MEASURED;
      const total = trades.reduce((s, t) => s + controllableAmount(t), 0);
      // ⚠ Divided by EVERY trade, not by the tagged ones. This is "what a
      // typical trade costs you in controllable losses"; dividing by the hits
      // would report the average size of a mistake and improve as you make
      // fewer, larger ones.
      return { value: total / trades.length, n: trades.length };
    },
    dollarImpact(trades, ctx) {
      const hits = trades.filter((t) => isControllableLoss(t, ctx.controllableTags));
      return {
        dollars: hits.reduce((s, t) => s + controllableAmount(t), 0),
        n: hits.length,
        population: trades.length,
      };
    },
    contributors: (trades, ctx) =>
      trades.filter((t) => isControllableLoss(t, ctx.controllableTags)),
    // ⚠ THE REASON IS THE WHOLE POINT OF THIS LIST. Three revenge re-entries after
    // one red session need a different rule from five FOMO chases, and the
    // dollars alone cannot tell them apart.
    reviewColumn: (_trades, ctx) => ({
      header: 'Reason',
      unit: 'text',
      value: (t) => controllableLossReasons(t, ctx.controllableTags).join(', ') || null,
    }),
  },
  {
    id: 'tilt-after-loss', eduKey: 'coach.leak.tilt-after-loss',
    label: 'Tilt — win-rate drop after a loss',
    help: 'How much worse you trade the next setup once you are red.',
    kind: 'realized', better: 'lower', floor: 0, unit: 'pct', minSample: 8,
    available: true, dollarsAvailable: true,
    measure: (trades) => tiltDrop(trades),
    dollarImpact: (trades) => tiltDollars(trades),
    // ⚠ NO EXTRA COLUMN. Every row on this list is an after-loss trade that lost;
    // the net P&L already on the table is the number that flagged it, and a
    // second copy of it under another heading would be D102's exact failure.
    contributors: (trades) => tiltContributors(trades),
  },
  {
    id: 'premature-exit', eduKey: 'coach.leak.premature-exit',
    label: 'Cost of premature exit',
    help: 'What holding would have been worth on exits that never touched your '
      + 'stop, minus the cost of the ones that would have been stopped out.',
    needs: 'a computed post-exit run — recompute Trade Extension in the old client',
    kind: 'opportunity', better: 'lower', floor: 0, unit: 'usd', minSample: 15,
    available: true, dollarsAvailable: true,
    // ⚠ THE RATE IS THE NET PER TRADE, not the total. A ten-day "since" window
    // always shows a smaller TOTAL than a sixty-day baseline, so proving on
    // totals manufactures an improvement out of a shorter window.
    measure(trades, ctx) {
      const r = prematureExitNet(trades, ctx.contracts ?? []);
      return { value: r.population ? r.net / r.population : null, n: r.population };
    },
    dollarImpact(trades, ctx) {
      const r = prematureExitNet(trades, ctx.contracts ?? []);
      // ⚠ `stoppedPct` MUST TRAVEL WITH THE DOLLARS — see `LeakDollars.meta`.
      // This is the most dangerous number in the report without it.
      return {
        dollars: r.net, n: r.favN, population: r.population,
        meta: { stoppedPct: r.stoppedPct, upperBound: true },
      };
    },
    // ⚠ THE EXITS THAT RAN FAVOURABLY WITHOUT HITTING THE STOP. Not every trade
    // with an extension — the stopped ones are the evidence AGAINST holding, and
    // putting them on a list headed "money you left" would invert the finding.
    contributors: (trades, ctx) => prematureExitNet(trades, ctx.contracts ?? []).favTrades,
    reviewColumn: (_trades, ctx) => ({
      header: 'Ran after',
      tone: 'green',
      unit: 'usd',
      value: (t) => {
        const pv = prematureExitValue(t as never, ctx.contracts ?? []);
        return pv && pv.value > 0 ? pv.value : null;
      },
    }),
  },
  {
    id: 'mfe-capture', eduKey: 'coach.leak.mfe-capture',
    label: 'Capture efficiency',
    help: 'The median share of the peak favourable move you kept, on trades that '
      + 'reached 1R.',
    needs: 'chart or excursion data',
    kind: 'opportunity', better: 'higher', ceiling: 100, unit: 'pct', minSample: 10,
    // ⚠ The rate measures; the dollar face does not. See `dollarsAvailable`.
    available: true, dollarsAvailable: false,
    measure(trades) {
      const r = mfeCapture(trades);
      return { value: r.pct, n: r.n };
    },
    // ⚠ NO DOLLAR FACE YET. Give-back is per-trade here and the aggregate is not
    // ported; reporting zero would place a real leak at the BOTTOM of the
    // ranking, which is worse than placing it nowhere. The row shows its rate
    // and says the dollars are not computed.
    dollarImpact: () => NO_DOLLARS,
    // ⚠ AND SO NO REVIEW LIST EITHER, deliberately. The old client's list is
    // "the trades that gave back the most dollars", which is the aggregate this
    // client has not built — and it needs a contract catalogue for the
    // dollars-per-point, plus `mfe_secs`, `gross_pnl` and `fees`, none of which
    // the report's read carries. Ranking by capture PERCENT instead would be a
    // different population wearing the same label.
    contributors: () => [],
  },
  {
    id: 'hold-disposition', eduKey: 'coach.leak.hold-disposition',
    label: 'Riding losers, cutting winners',
    help: 'The disposition effect — losers held longer than winners. Read from '
      + 'your fill times; no chart data needed.',
    kind: 'realized', better: 'lower', unit: 'min', minSample: 5,
    available: true, dollarsAvailable: true,
    measure: (trades) => dispositionRate(trades),
    dollarImpact: (trades) => dispositionDollars(trades),
    contributors: (trades) => dispositionStats(trades).riddenTrades,
    // ⚠ THE HEADER CARRIES THE BAR EACH ROW BEAT. "Held vs 23m" makes the list
    // self-evident — every hold time under it sits above 23 minutes, which is
    // exactly why the trade is here. A bare "Held" would leave the reader
    // guessing at the threshold.
    reviewColumn: (trades) => {
      const wm = dispositionStats(trades).winMedian;
      return {
        header: wm !== null ? `Held vs ${Math.round(wm)}m` : 'Held',
        unit: 'min',
        value: (t) => holdMinutes(t),
      };
    },
  },
  {
    id: 'add-to-loser', eduKey: 'coach.leak.add-to-loser',
    label: 'Adding to losers',
    help: 'Averaging down — adding at a worse price while underwater. Read from '
      + 'your fills; no chart data needed.',
    kind: 'realized', better: 'lower', floor: 0, unit: 'pct', minSample: 10,
    available: true, dollarsAvailable: true,
    measure: (trades) => addToLoserRate(trades),
    dollarImpact: (trades) => addToLoserDollars(trades),
    // ⚠ NO EXTRA COLUMN, matching the old client. What flagged these trades is
    // the SHAPE of the fills, not one number — the entry and exit already on the
    // table are where the reader starts, and the trade itself holds the rest.
    contributors: (trades) => addToLoserStats(trades).lostTrades,
  },
  {
    id: 'plan-shortfall', eduKey: 'coach.leak.plan-shortfall',
    label: 'Cutting winners short of plan',
    help: 'On your winners, how much of the reward YOU planned you actually '
      + 'took. From your logged R:R; no chart data needed.',
    kind: 'opportunity', better: 'higher', ceiling: 100, unit: 'pct', minSample: 10,
    available: true, dollarsAvailable: true,
    measure: (trades) => planShortfallRate(trades),
    dollarImpact: (trades) => ({
      ...planShortfallDollars(trades),
      // ⚠ Hitting your target was never guaranteed just because you set it.
      meta: { upperBound: true },
    }),
    contributors: (trades) => planStats(trades).shortTrades,
    // ⚠ THE TARGET, read against the realized R already in the table. That pair
    // is the whole diagnosis: still trending when you left is cutting winners
    // early; stalled well short every time means the targets were fiction.
    reviewColumn: () => ({
      header: 'Planned R',
      unit: 'r',
      value: (t) => (Number(t.rrPlanned) > 0 ? Number(t.rrPlanned) : null),
    }),
  },
];

export const leakById = (id: string): LeakMetric | null =>
  LEAK_METRICS.find((l) => l.id === id) ?? null;

export interface LeakReviewSet {
  trades: LeakTrade[];
  column: LeakReviewColumn | null;
}

/**
 * The trades behind one leak, and the column that explains why each is there.
 *
 * ⚠ ONE CALL, so the list and its column can never come from different leaks.
 * They were separate lookups in the old client and the review drill had to pass
 * the id twice.
 */
export function leakReviewSet(
  leakId: string, trades: readonly LeakTrade[], ctx: LeakContext,
): LeakReviewSet | null {
  const leak = leakById(leakId);
  if (!leak) return null;
  return {
    trades: leak.contributors(trades, ctx),
    column: leak.reviewColumn ? leak.reviewColumn(trades, ctx) : null,
  };
}

/**
 * Net dollars per trade — the GUARDRAIL every rule is certified against.
 *
 * ── 🚨 WHY A RULE IS NEVER JUDGED ON ITS OWN NUMBER ──
 *
 * Every leak above is gameable by a rule that improves the metric while costing
 * money. "Cut every trade at −1R" collapses controllable losses; a blanket "hold
 * longer" shrinks premature exit even on a history where most exits were
 * followed by a reversal. Both would report a win while the account bled.
 *
 * So this is frozen beside the leak's own baseline at the moment a rule is
 * written, and compared again at Prove time. It is not a leak — it never appears
 * in either ledger — which is exactly why it cannot be gamed by the rule under
 * test.
 *
 * ⚠ A MISSING `netPnl` COUNTS AS ZERO AND STAYS IN THE SAMPLE, matching the old
 * client. Dropping those trades instead would raise or lower the mean depending
 * on which side they sat, and the baseline and the re-measure would disagree
 * about the population as soon as one of them had a gap.
 */
export function realizedExpectancy(trades: readonly LeakTrade[]): LeakRate {
  if (!trades.length) return NOT_MEASURED;
  const total = trades.reduce((s, t) => s + (Number(t?.netPnl) || 0), 0);
  return { value: total / trades.length, n: trades.length };
}

// ---------------------------------------------------------------------------
// Ranking
// ---------------------------------------------------------------------------

export interface RankedLeak {
  id: string;
  label: string;
  help: string;
  eduKey: string;
  kind: LeakKind;
  unit: LeakMetric['unit'];
  needs: string | null;
  available: boolean;
  dollarsAvailable: boolean;
  unitLabel: string;
  dollars: number;
  /** Trades that actually bled. */
  n: number;
  /** The sample the RATE was computed over. */
  population: number;
  rate: LeakRate;
  /** True when the leak's own qualifying sample is under its minimum. */
  thin: boolean;
  minSample: number;
  /** Share of the population where holding would have been stopped out. */
  stoppedPct: number | null;
  /** True when the dollar figure is a one-sided ceiling. */
  upperBound: boolean;
}

function rank(
  leak: LeakMetric, trades: readonly LeakTrade[], ctx: LeakContext,
): RankedLeak {
  const d = leak.available ? leak.dollarImpact(trades, ctx) : NO_DOLLARS;
  const rate = leak.available ? leak.measure(trades, ctx) : NOT_MEASURED;
  return {
    id: leak.id,
    label: leak.label,
    help: leak.help,
    eduKey: leak.eduKey,
    kind: leak.kind,
    unit: leak.unit,
    needs: leak.needs ?? null,
    available: leak.available,
    dollarsAvailable: leak.dollarsAvailable,
    unitLabel: leak.unit,
    dollars: Number(d.dollars) || 0,
    n: Number(d.n) || 0,
    population: Number(d.population) || 0,
    rate,
    // ⚠ Judged on the leak's OWN sample, not the trade count. Capture efficiency
    // only counts trades that reached 1R; tilt only counts sequenced pairs. A
    // 200-trade book can still be thin for a given leak.
    thin: leak.available && rate.value !== null && rate.n < leak.minSample,
    minSample: leak.minSample,
    stoppedPct: d.meta?.stoppedPct ?? null,
    upperBound: d.meta?.upperBound === true,
  };
}

export interface LeakRanking {
  realized: RankedLeak[];
  opportunity: RankedLeak[];
}

/**
 * Both ledgers, each sorted by dollars, biggest first.
 *
 * ⚠ UNAVAILABLE AND ZERO-DOLLAR LEAKS STAY IN THE LIST. A row that says "no leak
 * here this period" is a finding; a row that says "we cannot see this yet" is an
 * admission. Dropping either would leave the trader reading a shorter list with
 * no way to tell which kind of silence it was.
 */
export function rankLeaks(
  trades: readonly LeakTrade[], ctx: LeakContext,
): LeakRanking {
  const rows = LEAK_METRICS.map((l) => rank(l, trades, ctx));
  const byKind = (k: LeakKind) => rows
    .filter((r) => r.kind === k)
    .sort((a, b) => b.dollars - a.dollars);
  return { realized: byKind('realized'), opportunity: byKind('opportunity') };
}

export interface LeakSample {
  n: number;
  wins: number;
  losses: number;
  /** ⚠ Its own count. A scratch is neither, and folding it either way is a lie. */
  scratches: number;
  net: number;
}

/**
 * The plainest facts about the trades on screen.
 *
 * ── WHY THIS EXISTS ──
 *
 * When not one leak can be measured, the report used to say only that. On an
 * account holding three losing trades taken inside two minutes for −$3,185 that
 * is true, useless, and reads as though the app had lost the data.
 *
 * These are the facts the app is entitled to state without measuring anything:
 * how many trades, how they finished, what they came to. No habit is named and
 * no cost is attributed — that is the whole distinction the empty state exists
 * to keep.
 *
 * ⚠ NOT A LEAK, and deliberately not in either ledger. It is a description of
 * the sample, which is why it never gets a rate, a minimum, or a Fix it button.
 */
export function leakSample(trades: readonly LeakTrade[]): LeakSample {
  let wins = 0; let losses = 0; let scratches = 0; let net = 0;
  for (const t of trades) {
    const p = Number(t?.netPnl) || 0;
    net += p;
    if (p > 0) wins += 1;
    else if (p < 0) losses += 1;
    else scratches += 1;
  }
  return { n: trades.length, wins, losses, scratches, net };
}

/**
 * Is this row's dollar figure something the app is entitled to print?
 *
 * ── 🚨 A COST NEEDS A MEASURED RATE BEHIND IT ──
 *
 * The two halves of a leak are computed separately: the RATE says the habit
 * exists, the DOLLARS say what it cost. They can disagree — tilt can total up
 * the money lost on trades taken after a loss while having too few of each leg
 * to establish any win-rate drop at all.
 *
 * Printing the total anyway attributes that money to a habit nobody has shown
 * you have. It also reads as a contradiction on one line, which is how it was
 * found: Josh's side-by-side showed **"−$2,602.50"** sitting beside **"Not
 * enough data to measure this period"** in the same row, and the report's
 * opening sentence had picked that leak as where to start.
 *
 * ⚠ A DELIBERATE DIVERGENCE. The old client gates the amount on the dollar
 * population alone, so it prints the total and says nothing about the missing
 * rate — the same overclaim, made quietly instead of visibly.
 *
 * ⚠ A THIN sample still counts. The rate measured; there is simply little of
 * it, and the row says so beside the number.
 */
export const leakDollarsStand = (r: RankedLeak): boolean =>
  r.available && r.rate.value !== null && r.dollars > 0;

/**
 * The leak to start on.
 *
 * ⚠ REALIZED BY DEFAULT, and it must stay that way. The report's opening line is
 * "this is money already gone" — leading with a counterfactual would open the
 * whole method on a hypothetical.
 *
 * ⚠ Returns null when the top leak cost NOTHING. "Start here: $0" is not a
 * starting point, and the section says the period was clean instead.
 *
 * ⚠ SKIPS PAST a leak whose rate never measured, rather than stopping. It is
 * ranked first because it has the biggest dollar total, but its own row shows an
 * em dash — so naming it would send the trader to a line with no number on it.
 * The next leak down is the biggest one the report can actually stand behind.
 */
export function topLeak(
  trades: readonly LeakTrade[], ctx: LeakContext, kind: LeakKind = 'realized',
): RankedLeak | null {
  return rankLeaks(trades, ctx)[kind].find(leakDollarsStand) ?? null;
}
